import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import { InfoBarContext } from "../../common/info";
import { GET } from "../../utils/fetchUtil";
import { isTouch } from "../../utils/screenUtil";
import InputField from "../../common/input/InputField";
import Tabs from "../../common/tabs/Tabs";
import Tab from "../../common/tabs/Tab";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../../common/hooks/useDebounce";
import Confirmed from "./overview/Confirmed";
import ConfirmationAwaited from "./overview/ConfirmationAwaited";
import NotConfirmed from "./overview/NotConfirmed";
import Loader from "../../common/loader/Loader";
import { MessageContext } from "../../common/message";
import Resolved from "./overview/Resolved";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { smoothScroll } from "../../utils/scrollbarUtil";
import { useQuery } from "../../common/hooks/useQuery";

function ResolvedRequestsPage() {
  const [request, setRequest] = useState(undefined);
  const [confirmed, setConfirmed] = useState([]);
  const [confirmationAwaited, setConfirmationAwaited] = useState([]);
  const [notConfirmed, setNotConfirmed] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [tabs, setTabs] = useState([]);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 250);
  const [activeJournalId, setActiveJournalId] = useState(null);
  const { t } = useTranslation();
  const infoBar = useContext(InfoBarContext);
  const message = useContext(MessageContext);
  const [elementScrollId, setElementScrollId] = useState();
  const queryParams = useQuery();

  const history = useHistory();

  const linkTab = history.location.state?.linkTab;
  const tab = history.location.state?.tab;

  // Given the props from history.push, scrolls to element's ID passed as prop.
  // Needs 150ms delay to scroll down.
  const location = useLocation();
  useEffect(() => {
    if (request) {
      if (location.state?.scrollTo) {
        setTimeout(() => {
          setActiveTab(3);
          location.state.scrollTo = null;
        }, 0);
      }
    }
  }, [request]);

  useEffect(() => {
    smoothScroll("#recit" + elementScrollId); //recitsec
  }, [elementScrollId]);

  useEffect(() => {
    setElementScrollId(queryParams.get("uid"));
  }, [queryParams]);

  useLayoutEffect(() => {
    setTimeout(() => {
      smoothScroll("#recit" + elementScrollId);
    }, 0);
  }, [elementScrollId, request]);

  useEffect(() => {
    GET(
      "/requests/submissions?status=3" +
        (debouncedQuery.length > 0 ? `&query=${debouncedQuery}` : ""),
      (requests) => {
        setRequest(requests);

        setConfirmed(
          requests.requests
            .sort(function (dateA, dateB) {
              return new Date(dateB.submissionDate) - new Date(dateA.submissionDate);
            })
            .filter(
              (req) =>
                req.statusText.includes("Riješeno (Potvrđeno)") &&
                req.title.toString().toLowerCase().includes(query)
            )
        );
        setConfirmationAwaited(
          requests.requests.filter(
            (req) =>
              req.statusText.includes("Riješeno (Čeka se potvrda)") &&
              req.title.toString().toLowerCase().includes(query)
          )
        );

        setNotConfirmed(
          requests.requests.filter(
            (req) =>
              req.statusText.includes("Riješeno (Nije potvrđeno u roku)") &&
              req.title.toString().toLowerCase().includes(query)
          )
        );

        setResolved(
          requests.requests.filter(
            (req) =>
              req.statusText.includes("Riješeno (Završen postupak)") &&
              req.title.toString().toLowerCase().includes(query)
          )
        );
      },
      (error) => {
        message.toggle({
          title: t("requests.reviewDocuments.action.save.error"),
          content: error,
          hideNo: true,
          yesLabel: t("modalMessage.okLabel"),
          onYes: () => {
            message.toggle({});
          },
        });
      }
    );

    return () => setRequest([]);
  }, [debouncedQuery, query]);

  useEffect(() => {
    if (!infoBar.isOpen) {
      setActiveJournalId(null);
    }
  }, [infoBar.isOpen, setActiveJournalId]);

  useLayoutEffect(() => {
    if (linkTab !== undefined) {
      setActiveTab(parseInt(linkTab));
    }
  }, [linkTab]);

  useLayoutEffect(() => {
    if (tab !== undefined) {
      setActiveTab(parseInt(tab));
    }
  }, [tab]);

  useEffect(() => {
    setTabs([
      t("requests.resolvedPage.confirmed.tab", { count: confirmed.length }),
      t("requests.resolvedPage.confirmationAwaited.tab", {
        count: confirmationAwaited.length,
      }),
      t("requests.resolvedPage.notConfirmed.tab", {
        count: notConfirmed.length,
      }),
      t("requests.resolvedPage.resolved.tab", {
        count: resolved.length,
      }),
    ]);
  }, [resolved, confirmed, confirmationAwaited, notConfirmed, t]);

  if (request) {
    return (
      <div className="ml-2 mr-2">
        <div className="mt-2 is-flex is-flex-direction-row-reverse">
          <div className="field control" style={{ width: isTouch() ? "100%" : "25%" }}>
            <InputField
              placeholder={t("requests.received.searchInputFieldPlaceholder")}
              onChange={(e) => setQuery(e.currentTarget.value)}
              value={query}
            />
          </div>
        </div>
        <Tabs setActiveTab={setActiveTab} activeTab={activeTab} scrollableContentHeight={812}>
          <Tab
            label={tabs[0]}
            render={() => (
              <Confirmed
                requests={confirmed}
                activeJournalId={activeJournalId}
                setActiveJournalId={setActiveJournalId}
                title={tabs[0]}
              />
            )}
          />
          <Tab
            label={tabs[1]}
            render={() => (
              <ConfirmationAwaited
                requests={confirmationAwaited}
                activeJournalId={activeJournalId}
                setActiveJournalId={setActiveJournalId}
                title={tabs[1]}
              />
            )}
          />
          <Tab
            label={tabs[2]}
            render={() => (
              <NotConfirmed
                requests={notConfirmed}
                activeJournalId={activeJournalId}
                setActiveJournalId={setActiveJournalId}
                title={tabs[2]}
              />
            )}
          />
          <Tab
            label={tabs[3]}
            render={() => (
              <Resolved
                requests={resolved}
                activeJournalId={activeJournalId}
                setActiveJournalId={setActiveJournalId}
                title={tabs[3]}
              />
            )}
          />
        </Tabs>
      </div>
    );
  } else {
    return <Loader />;
  }
}

export default ResolvedRequestsPage;
